import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LoginScreen = () => {
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('phone');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Show session-expired message
  const params = new URLSearchParams(window.location.search);
  const sessionExpired = params.get('session_expired') === '1';

  const handleSendOTP = async () => {
    if (phone.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/send-otp`, { phone });
      if (res.data.success) {
        toast.success('OTP sent successfully');
        if (res.data.dev_otp) {
          toast.info(`Dev OTP: ${res.data.dev_otp}`, { duration: 10000 });
        }
        setStep('otp');
      }
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Failed to send OTP';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otp.length < 4) {
      toast.error('Please enter OTP');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/verify-otp`, { phone, otp });
      if (res.data.success) {
        login(res.data.token, res.data.user, res.data.refresh_token);
        toast.success('Login successful!');
        const returnTo = sessionStorage.getItem('returnTo');
        sessionStorage.removeItem('returnTo');
        navigate(returnTo || '/home');
      }
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Invalid OTP';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F0F2F5] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg p-8"
        >
          {/* Logo/Title */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-extrabold text-[#128C7E] mb-2" data-testid="app-title">
              SellGen AI
            </h1>
            <p className="text-[#667781]">
              Create. Market. Rank. Engage. Sell.
            </p>
          </div>

          {sessionExpired && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-center" data-testid="session-expired-msg">
              <p className="text-sm text-amber-800 font-medium">Session expired</p>
              <p className="text-xs text-amber-600 mt-0.5">Please log in again to continue.</p>
            </div>
          )}

          {step === 'phone' ? (
            <div data-testid="phone-step">
              <label className="block text-base font-semibold text-slate-800 mb-3">
                Mobile Number
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="9876543210"
                maxLength={10}
                className="w-full h-16 px-4 bg-gray-50 border-2 border-gray-200 rounded-xl text-xl focus:ring-2 focus:ring-[#128C7E] focus:border-transparent outline-none transition-all mb-5"
                data-testid="phone-input"
              />
              <button
                onClick={handleSendOTP}
                disabled={loading}
                className="w-full h-16 bg-[#128C7E] text-white rounded-xl text-xl font-bold flex items-center justify-center gap-2 transition-transform active:scale-95 shadow-md disabled:opacity-50"
                data-testid="send-otp-button"
              >
                {loading ? 'Sending...' : 'Send OTP'}
              </button>
            </div>
          ) : (
            <div data-testid="otp-step">
              <label className="block text-base font-semibold text-slate-800 mb-3">
                Enter OTP sent to {phone}
              </label>
              <input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                placeholder="123456"
                maxLength={6}
                className="w-full h-16 px-4 bg-gray-50 border-2 border-gray-200 rounded-xl text-xl focus:ring-2 focus:ring-[#128C7E] focus:border-transparent outline-none transition-all mb-4 text-center tracking-widest"
                data-testid="otp-input"
              />
              <button
                onClick={handleVerifyOTP}
                disabled={loading}
                className="w-full h-16 bg-[#128C7E] text-white rounded-xl text-xl font-bold flex items-center justify-center gap-2 transition-transform active:scale-95 shadow-md disabled:opacity-50 mb-3"
                data-testid="verify-otp-button"
              >
                {loading ? 'Verifying...' : 'Verify & Login'}
              </button>
              <button
                onClick={() => setStep('phone')}
                className="w-full text-[#128C7E] font-semibold text-base"
                data-testid="change-number-button"
              >
                Change Number
              </button>
            </div>
          )}

          <p className="text-xs text-[#667781] text-center mt-6">
            By continuing, you agree to our Terms of Service
          </p>
        </motion.div>
      </div>
    </div>
  );
};
